/**
 * Main renderer initialization and management
 * Phase 4.1: PixiJS Renderer Setup
 */

import { createGlobeCanvas } from './globe-renderer.js';

/**
 * Position and draw projection border onto a Graphics object
 * @param {PIXI.Graphics} graphics - Graphics object to draw on
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} width - Projection width
 * @param {number} height - Projection height
 */
export function drawProjectionBorder(graphics, position, width, height) {
    graphics.x = position.x;
    graphics.y = position.y;
    graphics.lineStyle(3, 0xffffff, 0.25);
    graphics.drawRect(0, 0, width, height);
}

/**
 * Create dual renderer: projection (top center) + globe (below)
 * Phase 6: Dual Rendering System with vertical layout
 *
 * @param {HTMLElement} container - DOM element to attach canvas
 * @param {number} projectionWidth - Width for 2D projection viewport
 * @param {number} projectionHeight - Height for 2D projection viewport
 * @param {number} globeSize - Diameter for globe viewport
 * @param {string} initialProjection - Initial projection type (default: 'mercator')
 * @param {Object} positions - Pre-calculated positions for projection and globe
 * @param {Object} layout - Layout configuration (margins, etc.)
 * @returns {Promise<Object>} Dual renderer with projection and globe
 */
export async function createDualRenderer(container, projectionWidth, projectionHeight, globeSize, initialProjection = 'mercator', positions = {}, layout = {}) {
    const PIXI = await import('pixi.js');

    const totalWidth = window.innerWidth;
    const totalHeight = window.innerHeight;

    // Create single PixiJS app
    const app = new PIXI.Application({
        width: totalWidth,
        height: totalHeight,
        antialias: true,
        backgroundColor: 0x000000,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
    });

    container.appendChild(app.view);

    // Create projection renderer (positioned by layout calculator)
    const projectionContainer = new PIXI.Container();
    projectionContainer.x = positions.projection?.x || 0;
    projectionContainer.y = positions.projection?.y || 0;

    const projectionLayers = {
        background: new PIXI.Container(),
        countries: new PIXI.Container(),
    };

    projectionContainer.addChild(projectionLayers.background);
    projectionContainer.addChild(projectionLayers.countries);

    // Create ocean background for projection (respects aspect ratio)
    const projectionOcean = new PIXI.Graphics();
    projectionOcean.beginFill(0x1a1a2e);
    projectionOcean.drawRect(0, 0, projectionWidth, projectionHeight);
    projectionOcean.endFill();
    projectionLayers.background.addChild(projectionOcean);

    // Phase 6 Optimization: Add clipping mask to prevent rendering outside projection bounds
    // This prevents duplicated paths from bleeding into the globe area
    const projectionMask = new PIXI.Graphics();
    projectionMask.beginFill(0xffffff);
    projectionMask.drawRect(0, 0, projectionWidth, projectionHeight);
    projectionMask.endFill();
    projectionContainer.addChild(projectionMask);
    projectionContainer.mask = projectionMask;

    // Use pre-calculated globe position
    const globeX = positions.globe?.x || 0;
    const globeY = positions.globe?.y || 0;

    // Add projection container to stage
    app.stage.addChild(projectionContainer);

    // Border drawn outside the masked container so it isn't clipped
    const projectionBorder = new PIXI.Graphics();
    drawProjectionBorder(projectionBorder, positions.projection, projectionWidth, projectionHeight);
    app.stage.addChild(projectionBorder);

    // Create Canvas 2D element for globe (faster than WebGL for many polygons)
    const globeRadius = globeSize / 2;
    const { canvas: globeCanvas, ctx: globeCtx } = createGlobeCanvas(globeSize, globeX, globeY);
    container.appendChild(globeCanvas);

    return {
        app,
        projection: {
            container: projectionContainer,
            layers: projectionLayers,
            oceanGraphics: projectionOcean,
            mask: projectionMask,
            border: projectionBorder,
            width: projectionWidth,
            height: projectionHeight,
            PIXI,
        },
        globe: {
            canvas: globeCanvas,
            ctx: globeCtx,
            radius: globeRadius,
            size: globeSize,
            bounds: {
                x: globeX,
                y: globeY,
                width: globeSize,
                height: globeSize,
            },
        },
        PIXI,
    };
}

/**
 * Start the render loop
 * @param {Object} renderer - Renderer instance
 * @param {Function} updateCallback - Called each frame with delta time
 */
export function startRenderLoop(renderer, updateCallback) {
    renderer.app.ticker.add((delta) => {
        // delta is in frames, convert to seconds (assuming 60 FPS target)
        const deltaTime = delta / 60;
        updateCallback(deltaTime);
    });
}

/**
 * Stop the render loop
 * @param {Object} renderer - Renderer instance
 */
export function stopRenderLoop(renderer) {
    renderer.app.ticker.stop();
}

/**
 * Resume the render loop
 * @param {Object} renderer - Renderer instance
 */
export function resumeRenderLoop(renderer) {
    renderer.app.ticker.start();
}
